import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '../lib/cn';
import { formatDatum, formatQm } from '../lib/format';
import { type EinheitImHaus, type Struktur, geschossKurz, geschossLang, nachEtagen } from '../lib/haus';
import { IconPlus } from './icons';

export interface HausansichtProps {
  struktur: Struktur;
  einheiten: EinheitImHaus[];
  onOeffne: (einheitId: string) => void;
  /** Neue Einheit auf einer Etage anlegen. */
  onEinheitAnlegen?: (geschoss: number) => void;
  onEtagenZuordnen?: () => void;
  /** Nur Etagen mit Einheiten zeigen (Eigentumswohnung: die übrigen Etagen gehören Dritten). */
  nurBelegteEtagen?: boolean;
}

const TON: Record<string, { balken: string; flaeche: string; text: string }> = {
  vermietet: { balken: 'bg-success', flaeche: 'bg-surface', text: 'text-fg' },
  leer: { balken: 'bg-warning', flaeche: 'bg-warning-soft/40', text: 'text-warning-soft-fg' },
  eigennutzung: { balken: 'bg-accent', flaeche: 'bg-surface', text: 'text-fg' },
  // Noch nie vermietet (z. B. gerade angelegt): kein Alarm, sondern Aufforderung.
  offen: { balken: 'bg-border-strong', flaeche: 'bg-surface', text: 'text-fg-muted' },
};

function monatJahr(iso: string): string {
  const [j, m] = iso.split('-');
  return `${m}/${j}`;
}

/**
 * Das Haus Etage für Etage: oben das Dachgeschoss, unten die Untergeschosse, getrennt
 * durch die Geländelinie. Je Einheit die Belegung zum heutigen Tag.
 */
export function Hausansicht({
  struktur,
  einheiten,
  onOeffne,
  onEinheitAnlegen,
  onEtagenZuordnen,
  nurBelegteEtagen = false,
}: HausansichtProps) {
  const reduziert = useReducedMotion();
  const alle = nachEtagen(einheiten, struktur);
  const gruppen = nurBelegteEtagen ? alle.filter((g) => g.einheiten.length > 0) : alle;

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-sm">
      {/* Dach */}
      <div className="h-12 px-2 pt-3 sm:h-16 sm:px-3" aria-hidden="true">
        <svg viewBox="0 0 100 10" preserveAspectRatio="none" className="h-full w-full">
          <path
            d="M0.5 10 L50 0.6 L99.5 10 Z"
            className="fill-surface-2 stroke-border-strong"
            strokeWidth="1.5"
            vectorEffect="non-scaling-stroke"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div className="space-y-px bg-border/60">
        {gruppen.map((gruppe, gi) => {
          const istUg = gruppe.geschoss !== null && gruppe.geschoss < 0;
          const ersteUg = istUg && (gi === 0 || (gruppen[gi - 1].geschoss ?? 0) >= 0);
          const ohne = gruppe.geschoss === null;
          return (
            <div key={gruppe.geschoss ?? 'ohne'}>
              {ersteUg && (
                <div className="flex items-center gap-2 bg-surface px-3 py-1" aria-hidden="true">
                  <span className="h-0.5 flex-1 rounded bg-fg-subtle/60" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-fg-subtle">Gelände</span>
                  <span className="h-0.5 flex-1 rounded bg-fg-subtle/60" />
                </div>
              )}
              {ohne && (
                <div className="flex flex-wrap items-center justify-between gap-2 bg-surface-2 px-3 py-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-fg-subtle">Ohne Etagenzuordnung</p>
                  {onEtagenZuordnen && (
                    <button type="button" onClick={onEtagenZuordnen} className="text-xs font-medium text-accent hover:underline">
                      Etagen zuordnen
                    </button>
                  )}
                </div>
              )}
              <motion.section
                aria-label={geschossLang(gruppe.geschoss)}
                initial={reduziert ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.28, delay: reduziert ? 0 : gi * 0.05, ease: [0.2, 0.8, 0.2, 1] }}
                className={cn(
                  'grid grid-cols-[2.75rem_1fr] gap-2 px-2 py-2 sm:grid-cols-[4.5rem_1fr] sm:gap-3 sm:px-3',
                  istUg ? 'bg-surface-2' : 'bg-surface',
                )}
              >
                <div className="flex flex-col justify-center">
                  {!ohne && (
                    <>
                      <span className="text-sm font-semibold text-fg tabellenziffern">{geschossKurz(gruppe.geschoss)}</span>
                      <span className="hidden text-[11px] text-fg-subtle sm:block">
                        {gruppe.einheiten.length === 0
                          ? 'keine Einheit'
                          : `${gruppe.einheiten.length} ${gruppe.einheiten.length === 1 ? 'Einheit' : 'Einheiten'}`}
                      </span>
                    </>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {gruppe.einheiten.map((e) => (
                    <Kachel key={e.id} e={e} onOeffne={onOeffne} reduziert={!!reduziert} />
                  ))}
                  {onEinheitAnlegen && gruppe.geschoss !== null && (
                    <button
                      type="button"
                      onClick={() => onEinheitAnlegen(gruppe.geschoss as number)}
                      aria-label={`Einheit im ${geschossLang(gruppe.geschoss)} anlegen`}
                      className={cn(
                        'flex items-center justify-center rounded-lg border border-dashed border-border-strong text-fg-subtle',
                        'transition-colors duration-fast hover:border-accent hover:text-accent',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                        gruppe.einheiten.length === 0 ? 'min-h-[4.5rem] flex-1 gap-1.5 text-sm' : 'min-h-[4.5rem] w-7 sm:w-11',
                      )}
                    >
                      <IconPlus groesse={16} />
                      {gruppe.einheiten.length === 0 && <span>Einheit anlegen</span>}
                    </button>
                  )}
                </div>
              </motion.section>
            </div>
          );
        })}
      </div>

      {/* Fundament + Legende */}
      <div className="border-t-2 border-fg-subtle/50 px-3 py-2.5">
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-fg-muted">
          <li className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-success" /> Vermietet
          </li>
          <li className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-warning" /> Leerstand
          </li>
          <li className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm bg-accent" /> Eigennutzung
          </li>
        </ul>
      </div>
    </div>
  );
}

/** Kurzname auf der Kachel: Die Etage steht schon links — bei „2. OG links“ genügt „links“. */
function kachelName(e: EinheitImHaus): string {
  const praefix = geschossKurz(e.geschoss);
  if (e.geschoss !== null && e.bezeichnung.startsWith(praefix)) {
    const rest = e.bezeichnung.slice(praefix.length).trim();
    return rest || e.bezeichnung;
  }
  return e.bezeichnung;
}

function Kachel({ e, onOeffne, reduziert }: { e: EinheitImHaus; onOeffne: (id: string) => void; reduziert: boolean }) {
  const b = e.belegung;
  const nieVermietet = b.status === 'leer' && !b.leerSeit && !b.naechsterEinzug;
  const ton = TON[nieVermietet ? 'offen' : b.status];
  const auszug = b.status === 'vermietet' && b.ende ? b.ende : null;

  let titel: string;
  let unter: string;
  if (b.status === 'vermietet') {
    titel = b.mieterName ?? 'Mietverhältnis';
    unter = [b.beginn ? `seit ${monatJahr(b.beginn)}` : null, b.personenzahl ? `${b.personenzahl} Pers.` : null]
      .filter(Boolean)
      .join(' · ');
  } else if (b.status === 'eigennutzung') {
    titel = 'Eigennutzung';
    unter = '';
  } else if (nieVermietet) {
    titel = 'Kein Mietverhältnis';
    unter = 'Mieter erfassen';
  } else {
    titel = 'Leerstand';
    unter = b.leerSeit ? `seit ${formatDatum(b.leerSeit)}` : 'nicht vermietet';
  }

  return (
    <motion.button
      type="button"
      onClick={() => onOeffne(e.id)}
      whileHover={reduziert ? undefined : { y: -2 }}
      whileTap={reduziert ? undefined : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 500, damping: 32 }}
      aria-label={`${e.bezeichnung}: ${titel}${unter ? `, ${unter}` : ''}`}
      className={cn(
        'relative flex min-h-[4.5rem] min-w-[7rem] flex-1 basis-[7rem] overflow-hidden rounded-lg border border-border text-left shadow-sm sm:basis-[10rem]',
        'transition-shadow duration-fast hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        ton.flaeche,
      )}
    >
      <span className={cn('w-1 shrink-0', ton.balken)} aria-hidden="true" />
      <span className="min-w-0 flex-1 px-2.5 py-2">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate text-[11px] font-medium uppercase tracking-wide text-fg-subtle">{kachelName(e)}</span>
          <span className="shrink-0 text-[11px] text-fg-subtle tabellenziffern">{formatQm(e.wohnflaecheQm)}</span>
        </span>
        <span className={cn('mt-0.5 block truncate text-sm font-semibold', ton.text)}>{titel}</span>
        {unter && <span className="block text-xs leading-snug text-fg-muted">{unter}</span>}
        {auszug && (
          <span className="mt-1 inline-block rounded bg-warning-soft px-1.5 py-0.5 text-[10px] font-medium text-warning-soft-fg">
            Auszug {formatDatum(auszug)}
          </span>
        )}
        {b.status !== 'vermietet' && b.naechsterEinzug && (
          <span className="mt-1 inline-block rounded bg-accent-soft px-1.5 py-0.5 text-[10px] font-medium text-accent-soft-fg">
            Einzug {formatDatum(b.naechsterEinzug)}
          </span>
        )}
        {e.nutzungsart === 'gewerbe' && (
          <span className="mt-1 ml-1 inline-block rounded bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-fg-muted">
            Gewerbe
          </span>
        )}
      </span>
    </motion.button>
  );
}
